import React, { useEffect, useMemo, useState } from 'react';
import { Button, ButtonGroup, Col, Container, Row, Spinner } from 'react-bootstrap';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';
import dayjs from 'dayjs';
import Filter from 'Components/Common/Filter/Filter';
import { TableReport } from 'Components/Page/InformationAccountant/index';
import { jasracApi } from 'Datasource/JasracApi';
import { exportExcel } from 'Utils/File';
import { DATE_FORMAT_7, DATE_FORMAT_5, DATE_FORMAT_YM } from 'Constant/Date';
import JasacSearch from './SearchJasrac';
import useMessage from 'Hooks/useMessage';
import Tabs from 'Components/Common/Tabs/Tabs';
import 'Static/Styles/report.scss';
import TableReportNxt from 'Components/Page/InformationAccountant/TableReportNxt';
import TableReportVideo from 'Components/Page/InformationAccountant/TableReportVideo';
import TableReportNxtVideo from 'Components/Page/InformationAccountant/TableReportNxtVideo';
import useErrorBlob from 'Hooks/useErrorBlob';
import { convertValueMusicType, convertValueSongType } from 'Utils/Jasrac';

const btnGroupOptionsSong = [
  { name: 'JASRAC', value: '1' },
  { name: 'NexTone', value: '2' },
  { name: '自己管理', value: '3' },
  { name: 'その他', value: '4' },
];

const btnGroupOptionsType = [
  { name: '楽曲', value: '楽曲' },
  { name: '動画', value: '動画' },
  { name: '歌詞', value: '歌詞' },
];

export default function Jasrac(): JSX.Element {
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    status: StringParam,
    queryCompany: StringParam,
    companyIds: StringParam,
    type: StringParam,
    musicType: StringParam,
    groupDate: StringParam,
  });
  const [dataTable, setDataTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const page = query.page ?? 1;
  const { openMessageError } = useMessage();
  const { openErrorBlob } = useErrorBlob();

  const [groupDate, setGroupDate] = useState<any>(query.groupDate || new Date());

  const [type, setType] = useState(1);
  const [musicType, setMusicType] = useState(query.musicType || btnGroupOptionsType[0].value);
  const [exportingExcel, setExportingExcel] = useState<boolean>(false);
  const [exportingTsv, setExportingTsv] = useState<boolean>(false);

  const [tabActiveSong, setTabActiveSong] = useState(query.type || btnGroupOptionsSong[0].name);
  const [tabActiveType, setTabActiveType] = useState(
    query.musicType || btnGroupOptionsType[0].name,
  );

  const handleChangeTabSong = (tab: string) => {
    if (!tab) return;
    setTabActiveSong(tab);
    setQuery({ type: tab });
    if (tab === 'JASRAC') {
      setType(1);
    }
    if (tab === 'NexTone') {
      setType(2);
    }
    if (tab === '自己管理') {
      setType(3);
    }
    if (tab === 'その他') {
      setType(4);
    }
  };

  const handleChangeTabType = (tab: string) => {
    if (!tab) return;
    setTabActiveType(tab);
    setMusicType(tab);
    setQuery({ musicType: tab });
  };

  const handleGetJasrac = async (ids: any) => {
    try {
      setLoading(true);
      const startDate = dayjs(groupDate).startOf('month').format(DATE_FORMAT_7);
      const endDate = dayjs(groupDate).endOf('month').format(DATE_FORMAT_7);
      const authorizedType = type ?? 1;
      let categoryType;
      if (musicType === '楽曲') {
        categoryType = 2;
      }
      if (musicType === '動画') {
        categoryType = 1;
      }
      if (musicType === '歌詞') {
        categoryType = 3;
      }
      const param = {
        startDate: startDate,
        endDate: endDate,
        page: page,
        type: type,
        limit: 20,
        authorizedType,
        categoryType,
      };
      if (ids?.companyIds) {
        Object.assign(param, { companyId: ids?.companyIds });
      }
      const {
        data: { data },
      } = await jasracApi.getList(param);
      if (musicType === '楽曲') {
        setDataTable(data.dataMusicReport);
      }
      if (musicType === '動画') {
        setDataTable(data.dataVideoReport);
      }
      if (musicType === '歌詞') {
        setDataTable(data.dataLyricReport);
      }
    } catch (error) {
      openMessageError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (btnType: string) => {
    const startDate = dayjs(groupDate).startOf('month').format(DATE_FORMAT_7);
    const endDate = dayjs(groupDate).endOf('month').format(DATE_FORMAT_7);
    const page = query?.page ?? 1;
    const limit = 100;
    const authorizedType = type ?? 1;
    let categoryType;
    if (musicType === '楽曲') {
      categoryType = 2;
    }
    if (musicType === '動画') {
      categoryType = 1;
    }
    if (musicType === '歌詞') {
      categoryType = 3;
    }
    let outputOption;
    if (musicType === '楽曲') {
      outputOption = 'dataMusicReport';
    }
    if (musicType === '動画') {
      outputOption = 'dataVideoReport';
    }
    if (musicType === '歌詞') {
      outputOption = 'dataLyricReport';
    }
    const param = { startDate, endDate, authorizedType, categoryType, page, limit, outputOption };
    if (query?.companyIds) {
      Object.assign(param, { companyId: query?.companyIds });
    }

    try {
      if (btnType === 'tsv') {
        setExportingTsv(true);
        await exportExcel(
          jasracApi.exportTSV(param),
          `著作権報告書_${convertValueSongType(type)}_${convertValueMusicType(musicType)}_${dayjs(
            startDate,
          )
            .format(DATE_FORMAT_YM)
            .toString()}`,
          'tsv',
        );
      }
      if (btnType === 'excel') {
        setExportingExcel(true);
        await exportExcel(
          jasracApi.exportExcel(param),
          `著作権報告書_${convertValueSongType(type)}_${convertValueMusicType(musicType)}_${dayjs(
            startDate,
          )
            .format(DATE_FORMAT_YM)
            .toString()}`,
        );
      }
    } catch (error) {
      openErrorBlob(error);
    } finally {
      setExportingExcel(false);
      setExportingTsv(false);
    }
  };

  const handleChangeDate = (isAdd = false) => {
    const date = isAdd ? dayjs(groupDate).add(1, 'month') : dayjs(groupDate).subtract(1, 'month');
    setQuery({ groupDate: dayjs(date).format(DATE_FORMAT_7) });
    setGroupDate(date);
  };

  useEffect(() => {
    const ids = {
      companyIds: query.companyIds ?? '',
    };
    handleGetJasrac(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, query.companyIds, groupDate, type, musicType]);

  /**
   * save query params
   */
  useEffect(() => {
    if (query.musicType) {
      setTabActiveType(query.musicType);
      setMusicType(query.musicType);
    }

    if (query.type) {
      setTabActiveSong(query.type);
      if (query.type === 'JASRAC') {
        setType(1);
      }
      if (query.type === 'NexTone') {
        setType(2);
      }
      if (query.type === '自己管理') {
        setType(3);
      }
      if (query.type === 'その他') {
        setType(4);
      }
    }

    if (query.groupDate) {
      setGroupDate(dayjs(query.groupDate));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderTable = useMemo(() => {
    if (tabActiveSong !== 'NexTone') {
      if (musicType !== '動画') {
        return <TableReport dataTable={dataTable} loading={loading} mediaType={tabActiveType} />;
      } else {
        return (
          <TableReportVideo dataTable={dataTable} loading={loading} mediaType={tabActiveType} />
        );
      }
    } else {
      if (musicType !== '動画') {
        return <TableReportNxt dataTable={dataTable} loading={loading} mediaType={tabActiveType} />;
      } else {
        return (
          <TableReportNxtVideo dataTable={dataTable} loading={loading} mediaType={tabActiveType} />
        );
      }
    }
  }, [tabActiveSong, musicType, dataTable, loading, tabActiveType]);

  return (
    <>
      <Container fluid className="always-show-scrollbar">
        <Filter filterClass="shadow-sm">
          <Row>
            <JasacSearch />
          </Row>
          <Row className="mt-3">
            <div className="display-content p-0">
              <Col md="6" className="width-content">
                <Row>
                  <Col className="display-tab-content p-0">
                    <div className="align-self-center">
                      <Tabs
                        groupClass="btn-group-statistic mt-1"
                        options={btnGroupOptionsSong}
                        tabActive={tabActiveSong}
                        onChange={handleChangeTabSong}
                        clickOnly
                        disabledSetQuery
                      />
                    </div>
                    <div className="align-self-center padding-tab-content margin-top-1rem">
                      <Tabs
                        groupClass="btn-group-statistic mt-1"
                        options={btnGroupOptionsType}
                        tabActive={tabActiveType}
                        onChange={handleChangeTabType}
                        clickOnly
                        disabledSetQuery
                      />
                    </div>
                  </Col>
                </Row>
              </Col>
              <Col md="6" className="width-content">
                <div className="display-btn-content">
                  <div className="btn-content-report">
                    <div className="margin-top-1rem">
                      <Button
                        className={`mt-1 d-flex prize-btn-excel justify-content-center align-items-center me-3 button-submit ${
                          exportingTsv ? 'pe-none' : ''
                        }`}
                        onClick={() => handleExport('tsv')}
                        style={{ height: '35px' }}
                      >
                        {exportingTsv ? (
                          <Spinner
                            animation="border"
                            variant="light"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : (
                          'TSVダウンロード'
                        )}
                      </Button>
                    </div>
                    <div className="margin-top-1rem">
                      <Button
                        className={`mt-1 d-flex prize-btn-excel justify-content-center align-items-center me-3 button-submit ${
                          exportingExcel ? 'pe-none' : ''
                        }`}
                        onClick={() => handleExport('excel')}
                        style={{ height: '35px' }}
                      >
                        {exportingExcel ? (
                          <Spinner
                            animation="border"
                            variant="light"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : (
                          'Excelダウンロード'
                        )}
                      </Button>
                    </div>
                    <ButtonGroup className="btn-group-date">
                      <Button
                        className="bg-white text-dark custom-border mt-1 d-flex justify-content-center align-items-center"
                        onClick={() => handleChangeDate(false)}
                      >
                        前月
                      </Button>
                      <Button
                        variant="dark"
                        className="bg-dark custom-border mt-1 d-flex justify-content-center align-items-center"
                      >
                        {dayjs(groupDate).format(DATE_FORMAT_5)}
                      </Button>
                      <Button
                        className="bg-white text-dark custom-border mt-1 d-flex justify-content-center align-items-center"
                        onClick={() => handleChangeDate(true)}
                      >
                        次月
                      </Button>
                    </ButtonGroup>
                  </div>
                </div>
              </Col>
            </div>
          </Row>
        </Filter>
        <Container fluid>{renderTable}</Container>
      </Container>
    </>
  );
}
